import moment from "moment";

import type {
  RelatedPublicTrip,
  RelatedPublicTripV4,
  RelatedPublicSegment,
  RelatedPublicSegmentV4,
  PublicTripLeg,
  PublicPriceGroup,
  PublicFare
} from "@busbud/core-types/dist/lib/poll-service/api";

import { POLICY_TYPES } from "@app/constants/terms";
import type { YieldifySegment } from "@app/helpers/third-parties/yieldify";
import { isOutboundLeg, isReturnLeg } from "@app/helpers/udm";
import { getDisplayPrice } from "@app/helpers/udm/get-display-price";
import { getCartV2TripItem } from "@app/modules/checkout/utils/cart-v2";
import type { PriceMode } from "@app/modules/results/constants/price-mode";
import type { Features } from "@app/types/experiments";
import type {
  NapiV2Cart,
  NapiV2CartSegment
} from "@app/types/napi-v2-cart-types";
import type { SearchInfo } from "@app/types/search-types";

import type { SupportedLocale } from "@config/locales";

import { removeTimeOffset } from "../../../helpers/time";
import {
  isBoXTrip,
  isBoBTrip,
  isInterconnection,
  hasChangeOfStation
} from "../../../helpers/udm/trip";
import { Money } from "../../../lib/money";
import * as PropertyValues from "../../constants/property-values";
import { LOW_AVAILABILITY_SEATS } from "../constants";

export function getTripInformation(
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  trip_leg: PublicTripLeg,
  segments:
    | Record<string, RelatedPublicSegment>
    | Record<string, RelatedPublicSegmentV4>
): string {
  if (isInterconnection(trip, trip_leg)) {
    return hasChangeOfStation(trip_leg, segments)
      ? PropertyValues.INTERCONNECTION_WITH_STATION_CHANGE
      : PropertyValues.INTERCONNECTION_SAME_STATION;
  }

  if (isBoBTrip(trip)) {
    return PropertyValues.BOB;
  }

  if (isBoXTrip(trip)) {
    return PropertyValues.BOX;
  }

  return PropertyValues.NODEAL;
}

export function getDepartureDate(
  first_segment:
    | RelatedPublicSegment
    | RelatedPublicSegmentV4
    | NapiV2CartSegment
): string {
  // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return removeTimeOffset(first_segment.departure_time.timestamp).split("T")[0];
}

export function getDepartureTime(
  first_segment:
    | RelatedPublicSegment
    | RelatedPublicSegmentV4
    | NapiV2CartSegment
) {
  const parts = first_segment.departure_time.timestamp.split("T");

  // Not suppose to happen, but don't want code to crash if it does
  if (parts.length < 2) {
    return PropertyValues.EMPTY_STRING;
  }

  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  return parts[1].slice(0, 5);
}

export function getHoursBeforeDeparture(
  departure_time: string,
  today_m: moment.Moment
) {
  today_m = today_m || moment();

  let hours_before_departure = moment(departure_time).diff(today_m, "hours");
  if (hours_before_departure > 24) {
    hours_before_departure = 24 * Math.floor(hours_before_departure / 24.0);
  }

  return hours_before_departure;
}

export function getMinutesBeforeDeparture(
  departure_time: string,
  today_m: moment.Moment
) {
  return moment(departure_time).diff(today_m || moment(), "minutes");
}

export function hasLowAvailability(
  segments: (
    | RelatedPublicSegment
    | RelatedPublicSegmentV4
    | NapiV2CartSegment
  )[]
): boolean {
  return segments.some(segment => {
    return (
      segment.vehicle?.available_seats === null ||
      (segment.vehicle?.available_seats &&
        segment.vehicle?.available_seats < LOW_AVAILABILITY_SEATS)
    );
  });
}

export function getAveragePricePerPassenger(
  trip: RelatedPublicTrip | RelatedPublicTripV4,
  user_country: string,
  user_region: string | undefined,
  locale: SupportedLocale,
  search: SearchInfo,
  price_mode: PriceMode,
  features: Partial<Features>
) {
  const display_price = getDisplayPrice(trip, "trip", {
    origin_country: search.origin_city?.country_code2,
    user_country,
    user_region,
    price_mode,
    locale,
    features
  });
  const price_money = new Money(display_price, {
    nb_units: search.adult + search.child + search.senior
  });

  return price_money.amountPerUnit;
}

export function isRefundable(fares: Record<string, PublicFare>) {
  return Object.values(fares).every(fare =>
    fare?.terms.refund_policies.some(
      ({ type }) => type !== POLICY_TYPES.no_refund
    )
  );
}

export const getTripPriceFees = (price_groups: PublicPriceGroup[]) =>
  price_groups.reduce(
    (acc, price_group) =>
      acc +
      price_group.prices.breakdown.supplier_fees +
      price_group.prices.breakdown.busbud_fees,
    0
  );

export const getTripPriceTaxes = (price_groups: PublicPriceGroup[]) =>
  price_groups.reduce(
    (acc, price_group) => acc + price_group.prices.breakdown.taxes,
    0
  );

const getDateTimeInfo = (
  timestamp: string | null | undefined,
  timezone: string | null | undefined
) => {
  if (timestamp && timezone) {
    const timestamp_without_timezone_offset = timestamp.substring(0, 19);
    const datetime = moment.tz(timestamp_without_timezone_offset, timezone);

    return {
      localDate: datetime.format("YYYY-MM-DD"),
      localTime: datetime.format("HH:mm:ss"),
      timezone: timezone,
      iterableFormat: datetime.format("YYYY-MM-DD HH:mm:ss Z")
    };
  }

  return {
    localDate: "",
    localTime: "",
    timezone: "",
    iterableFormat: ""
  };
};

export const getSegmentInfo = (
  cart: NapiV2Cart | null | undefined,
  direction: "outbound" | "return"
): YieldifySegment | undefined => {
  if (!cart) {
    return undefined;
  }

  const current_trip = getCartV2TripItem(cart.items)!;
  const leg = current_trip.trip_legs.find(trip_leg =>
    direction === "outbound"
      ? isOutboundLeg(trip_leg.leg)
      : isReturnLeg(trip_leg.leg)
  )!;

  if (!leg) {
    return undefined;
  }

  const leg_segments = leg.segment_ids!.map(
    (segment_id): NapiV2CartSegment =>
      current_trip.segments.find(segment => segment.id === segment_id)!
  );

  const first_segment = leg_segments[0];
  // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const origin = first_segment.origin;

  const last_segment = leg_segments[leg_segments.length - 1];
  // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const destination = last_segment.destination;

  if (!origin.city || !destination.city) {
    return undefined;
  }

  return {
    origin: {
      name: origin.name,
      city: {
        name: origin.city.name,
        geohash: origin.city.geohash
      }
    },
    destination: {
      name: destination.name,
      city: {
        name: destination.city.name,
        geohash: destination.city.geohash
      }
    },
    operator: {
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      name: first_segment.operator.name || "",
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      logo_url: first_segment.operator.logo_url || ""
    },
    departureDateTime: getDateTimeInfo(
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      first_segment.departure_time.timestamp,
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      first_segment.departure_time.timezone
    ),
    arrivalDateTime: getDateTimeInfo(
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      last_segment.arrival_time!.timestamp,
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      last_segment.arrival_time!.timezone
    )
  };
};
